import { Pencil, Plus, User } from 'lucide-react';
import type { CatchAllAssignee, TaskAssignmentRuleResponse } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCatchAllAssignmentRule } from '@/hooks/use-assignment-rules';
import { CATCH_ALL_ICONS } from './catch-all-icons';

interface CatchAllCardProps {
  /**
   * Called when the user wants to create a new catch-all (no row exists
   * yet). Page navigates to the editor in catch-all create mode.
   */
  onCreate: () => void;
  /**
   * Called when the user wants to edit the existing catch-all. Page
   * navigates to the editor with the catch-all rule's id.
   */
  onEdit: (rule: TaskAssignmentRuleResponse) => void;
}

/**
 * Pinned-above-the-table card showing the org's catch-all assignment.
 * Two visual states:
 *   - empty: a single "+ Add default" CTA in a muted card
 *   - configured: a card showing the assignee + enabled state with an
 *     edit affordance
 *
 * The catch-all card lives outside the priority-ordered rules table so
 * pagination can't hide it; it's always visible regardless of which
 * page of rules the user is on.
 */
export function CatchAllCard({ onCreate, onEdit }: CatchAllCardProps): React.ReactElement {
  const { data, isLoading } = useCatchAllAssignmentRule();
  const catchAll = data?.rule ?? null;

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="mt-2 h-3 w-64" />
      </div>
    );
  }

  if (!catchAll) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium">When no rules match</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Set a default assignment so unmatched tasks always go somewhere.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add default
          </Button>
        </div>
      </div>
    );
  }

  // Configured state — icon comes from the shared registry so adding
  // a new catch-all assignee variant fails to compile until an icon
  // is registered. `User` is the safe fallback if the server ever
  // returns a value outside the allow-list (it shouldn't).
  const Icon = CATCH_ALL_ICONS[catchAll.assignToType as CatchAllAssignee] ?? User;
  // The whole card is the click target — same shape as a clickable
  // table row — so the hover-only pencil isn't the only way in on a
  // touch device. The pencil stays a real button so there's still a
  // keyboard path; the card itself is a plain div (no role/tabIndex)
  // so it doesn't add a second tab stop for the same action.
  return (
    <div
      className="group cursor-pointer rounded-lg border bg-muted/30 p-4 transition-colors hover:bg-muted/50"
      onClick={() => onEdit(catchAll)}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">When no rules match</h3>
            {!catchAll.isEnabled && (
              <Badge variant="outline" className="text-xs">
                Disabled
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Assign to</span>
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Badge variant="outline" className="text-xs capitalize">
              {catchAll.assignToType}
            </Badge>
            {catchAll.assignToName && (
              <span className="truncate font-medium">{catchAll.assignToName}</span>
            )}
          </div>
        </div>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label="Edit default assignment"
          title="Edit default assignment"
          // No `transition-opacity`: Button's base `transition-all` already covers
          // opacity, and tailwind-merge would drop it in favour of the narrower class.
          className="shrink-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
          onClick={(e) => {
            // The card's own click handler also opens the editor; stop
            // the bubble so it isn't triggered twice.
            e.stopPropagation();
            onEdit(catchAll);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
